/* =========================================================
   HORTIGO OS V92 — assets/hg-doctor.js
   Doctor module:
   - select cultură (dCrop) + help (dCropHelp)
   - simptome textarea (dIn)
   - file input (dFile) with extra cost (photoExtra)
   - tier buttons (data-mod="d") -> basic/std/prem
   - total display (dCost)
   - run AI (btnRunD) -> out-d
   - loader in output
   - uses HG.consume(), HG.buildPromptDoctor(), HG.runAI()

   Requires: assets/hg-core.js (window.HG)
========================================================= */

(() => {
  "use strict";

  function $(root, id) {
    return root ? root.querySelector(`#${CSS.escape(id)}`) : null;
  }

  function qsa(root, sel) {
    return Array.from(root ? root.querySelectorAll(sel) : []);
  }

  function safeText(el, txt) {
    if (el) el.textContent = String(txt ?? "");
  }

  function bootDoctor() {
    const HG = window.HG;
    if (!HG || !HG.state || !HG.state.root) return;

    const root = HG.state.root;

    // Prevent double init on section reload
    if (root.getAttribute("data-hg-doctor-bound") === "1") return;
    root.setAttribute("data-hg-doctor-bound", "1");

    const dCrop = $(root, "dCrop");
    const dCropHelp = $(root, "dCropHelp");
    const dFile = $(root, "dFile");
    const dIn = $(root, "dIn");
    const dCost = $(root, "dCost");
    const btnRunD = $(root, "btnRunD");
    const outD = $(root, "out-d");

    const tierBtns = qsa(root, `.hg-tier[data-mod="d"]`);

    // ------- UI helpers -------
    function setOutLoading(on, msg) {
      if (!outD) return;

      // remove existing
      const old = outD.querySelector("[data-hg-out-loader]");
      if (old) old.remove();
      if (!on) return;

      const loader = document.createElement("div");
      loader.setAttribute("data-hg-out-loader", "1");
      loader.style.border = "1px solid rgba(2,44,16,.10)";
      loader.style.background =
        "linear-gradient(180deg, rgba(31,122,59,.06), rgba(255,255,255,.92))";
      loader.style.borderRadius = "16px";
      loader.style.padding = "14px";
      loader.style.margin = "0 0 12px";
      loader.style.display = "flex";
      loader.style.alignItems = "center";
      loader.style.gap = "10px";
      loader.style.fontWeight = "950";

      loader.innerHTML = `
        <span style="font-size:20px;">⏳</span>
        <span>${HG.ui.escapeHTML(msg || "Analizez simptome…")}
          <span data-hg-dots style="display:inline-block; min-width:18px;">…</span>
        </span>
      `;

      outD.prepend(loader);

      const dots = loader.querySelector("[data-hg-dots]");
      if (dots) {
        let i = 0;
        const t = setInterval(() => {
          if (!document.body.contains(loader)) {
            clearInterval(t);
            return;
          }
          i = (i + 1) % 4;
          dots.textContent = ".".repeat(i) || " ";
        }, 350);
      }
    }

    function applyOutReadability() {
      if (!outD || !HG.ui.applyOutputReadability) return;
      HG.ui.applyOutputReadability(outD);
    }

    // ------- Fill crops -------
    function fillCrops() {
      if (!dCrop) return;

      const crops = Array.isArray(HG.state.cfg?.crops) ? HG.state.cfg.crops : [];
      dCrop.innerHTML = `<option value="">-- Alege --</option>`;

      for (const c of crops) {
        const opt = document.createElement("option");
        opt.value = String(c.name || "");
        const reqTier = Number.isFinite(+c.tier) ? +c.tier : 0;
        const locked = reqTier > HG.state.user.tier;
        opt.disabled = locked;
        opt.textContent = locked ? `${HG.ui.tierIcon(reqTier)} ${c.name}` : c.name;
        dCrop.appendChild(opt);
      }

      const hint =
        HG.state.user.tier === 0
          ? "Tier 1 = 🔒 (membru), Tier 2 = 👑 (premium)."
          : HG.state.user.tier === 1
          ? "Tier 2 = 👑 (premium)."
          : "Ai acces complet.";

      safeText(dCropHelp, hint);
    }

    // ------- Cost calc -------
    function computeTotalCost() {
      const tier = String(HG.state.uiTier?.d || "basic");
      const costs = HG.getCosts();
      let total = tier === "basic" ? costs.basic : tier === "std" ? costs.std : costs.prem;

      const hasFile = !!(dFile && dFile.files && dFile.files.length > 0);
      const photoExtra = Number.isFinite(+HG.state.cfg?.photoExtra) ? +HG.state.cfg.photoExtra : 2;

      if (hasFile) total += photoExtra;

      return total;
    }

    function renderCost() {
      if (!dCost) return;
      const total = computeTotalCost();
      dCost.textContent = `Total: ${total} Cr`;
    }

    // ------- Tier buttons binding -------
    function bindTierButtons() {
      for (const btn of tierBtns) {
        btn.addEventListener("click", () => {
          if (btn.classList.contains("is-locked")) {
            alert(HG.state.user.pending ? "Plata incompletă." : "Upgrade necesar!");
            if (typeof HG.navTo === "function") HG.navTo("wall");
            return;
          }

          const tier = btn.getAttribute("data-tier");
          if (!tier) return;

          // UI active
          tierBtns.forEach((b) => b.classList.remove("is-active"));
          btn.classList.add("is-active");

          HG.state.uiTier = HG.state.uiTier || {};
          HG.state.uiTier.d = tier;

          renderCost();
        });
      }
    }

    // ------- Run -------
    async function runDoctor() {
      const tier = String(HG.state.uiTier?.d || "basic");

      const crop = String(dCrop?.value || "").trim();
      if (!crop) {
        alert("Selectează cultura!");
        return;
      }

      const sym = String(dIn?.value || "").trim();
      if (!sym) {
        alert("Descrie simptomele!");
        return;
      }

      const cost = computeTotalCost();

      if ((HG.state.user.tok || 0) < cost) {
        alert(`Ai nevoie de ${cost} credite.`);
        if (typeof HG.navTo === "function") HG.navTo("wall");
        return;
      }

      // consume first
      const ok = await HG.consume(cost);
      if (!ok) return;

      if (outD) outD.innerHTML = `<div class="hg-empty">Se generează…</div>`;
      setOutLoading(true, "Analizez simptome… verific diagnostic diferențial…");

      try {
        const hasFile = !!(dFile && dFile.files && dFile.files[0]);
        const prompt = HG.buildPromptDoctor({
          tier,
          crop,
          symptoms: sym
        });

        const raw = await HG.runAI({
          type: hasFile ? "TEXT_IMAGE" : "TEXT",
          prompt,
          file: hasFile ? dFile.files[0] : null
        });

        const safe = HG.ui.sanitizeAIHtml(raw);
        if (outD) outD.innerHTML = safe || `<div class="hg-empty">Fără conținut.</div>`;
        applyOutReadability();
      } catch (e) {
        if (outD) outD.innerHTML = `<div class="hg-empty">Eroare conexiune.</div>`;
      } finally {
        setOutLoading(false);
      }
    }

    // ------- Init -------
    function init() {
      // default tier if not set
      HG.state.uiTier = HG.state.uiTier || {};
      if (!HG.state.uiTier.d) HG.state.uiTier.d = "basic";

      bindTierButtons();

      if (btnRunD) btnRunD.addEventListener("click", runDoctor);
      if (dFile) dFile.addEventListener("change", renderCost);
      if (dIn) dIn.addEventListener("input", () => {
        // optional: could update something later
      });

      fillCrops();
      Promise.resolve(HG.ensureConfig?.())
        .then(() => fillCrops())
        .catch(() => {});

      renderCost();
    }

    init();
  }

  function bootSafe() {
    if (window.HG && window.HG.state && window.HG.state.root) bootDoctor();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootSafe);
  } else {
    bootSafe();
  }

  document.addEventListener("shopify:section:load", () => {
    try { bootSafe(); } catch (_) {}
  });
})();
